import {
    ActionIcon,
    Anchor,
    Badge,
    Button,
    Checkbox,
    Divider,
    Group,
    Image,
    InputWrapper,
    rem,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from "@mantine/core";
import { useField } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconHelpCircle, IconInfoCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import IntlTelInput from "intl-tel-input/react";
import { useState } from "react";
import { useAddEud, useRemoveEud, useResendVerifyEud, useVerifyEud } from "shared/graphql/customer";
import { features } from "shared/graphql/features";
import { useUser, useUserEuds } from "shared/graphql/users";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useUserId } from "shared/stores/oidc";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { Cm, EudLikeFragment } from "src/gql/graphql";
import { match } from "ts-pattern";
import { z } from "zod";

function openVerifyEudModal({ type, id, value }: { type: Cm; id: string; value: string }) {
    modals.open({
        title: `Verify ${cmToLabel(type)}`,
        children: <VerifyEudModal eudId={id} eudValue={value} />,
        centered: true,
    });
}

const emailSchema = z.object({ email: z.string().email("Invalid email") });

const AddEudForm = ({ deviceType, onSubmit }: { deviceType: Cm.Email | Cm.Sms | Cm.Phone; onSubmit?: () => void }) => {
    const { addEudAsync } = useAddEud();

    const { userId } = useUserId();

    const [eudValue, setEudValue] = useState("");
    const [emailError, setEmailError] = useState<string>();
    const [phoneIsValid, setPhoneIsValid] = useState<boolean>();
    const [phoneErrorText, setPhoneErrorText] = useState<string>();

    const extension = useField({
        initialValue: "",
        validate: (extension) => {
            if (extension.length == 0 || /^([0-9#*W])+$/.test(extension)) return null;
            return "Valid characters: 0-9, *, #, W";
        },
        validateOnChange: true,
    });

    const telInputInitProps = {
        initialCountry: "us" as const,
        loadUtils: () => import("intl-tel-input/utils"),
    };

    return (
        <Stack>
            <Group>
                {deviceType == Cm.Phone && (
                    <Group align="flex-end">
                        <InputWrapper error={phoneErrorText}>
                            <Stack gap={0}>
                                <Text component="label" fz="sm">
                                    Receive Call:
                                </Text>
                                <IntlTelInput
                                    initOptions={telInputInitProps}
                                    onChangeNumber={setEudValue}
                                    onChangeValidity={setPhoneIsValid}
                                    inputProps={{ style: { height: rem(36) } }}
                                />
                            </Stack>
                        </InputWrapper>
                        <TextInput
                            label={
                                <Group gap="xs">
                                    Ext.
                                    <Tooltip label="Accepts 0-9, *, and # characters. Add 'W's to insert a 1 second pause in extension readout.">
                                        <IconHelpCircle size={16} />
                                    </Tooltip>
                                </Group>
                            }
                            {...extension.getInputProps()}
                        />
                    </Group>
                )}
                {deviceType == Cm.Sms && (
                    <InputWrapper error={phoneErrorText}>
                        <Stack gap={0}>
                            <Text component="label" fz="sm">
                                Receive Text:
                            </Text>
                            <IntlTelInput
                                initOptions={telInputInitProps}
                                onChangeNumber={setEudValue}
                                onChangeValidity={setPhoneIsValid}
                                inputProps={{ style: { height: rem(36) } }}
                            />
                        </Stack>
                    </InputWrapper>
                )}
                {deviceType == Cm.Email && (
                    <TextInput
                        w={250}
                        label="Email"
                        placeholder="example@gmail.com"
                        error={emailError}
                        onChange={(e) => setEudValue(e.currentTarget.value)}
                    />
                )}
            </Group>
            <Button
                onClick={() => {
                    if (deviceType == Cm.Email) {
                        try {
                            emailSchema.parse({ email: eudValue });
                        } catch {
                            setEmailError("Invalid email");
                            return;
                        }
                    } else {
                        if (!phoneIsValid) {
                            setPhoneErrorText("Invalid phone number");
                            return;
                        }
                        if (extension.error != null) return;
                    }

                    const ext = extension.getValue();
                    const fullNum = ext.length > 0 ? eudValue + ";" + ext : eudValue;

                    void addEudAsync({ userId, params: { type: deviceType, value: fullNum } })
                        .then((res) => {
                            onSubmit?.();
                            if (res.addEUD.optOut.system)
                                return openSmsOptOutModal({ value: res.addEUD.value, optOuts: res.addEUD.optOut.sms });

                            openVerifyEudModal({ type: deviceType, ...res.addEUD });
                        })
                        .catch(notify.catch);
                }}
            >
                Send Verification Code
            </Button>
            <Divider />
        </Stack>
    );
};

const VerifyEudModal = ({ eudId, eudValue }: { eudId: string; eudValue: string }) => {
    const { userId } = useUserId();
    const { verifyEudAsync } = useVerifyEud();
    const { resendVerifyEud } = useResendVerifyEud();

    const verifyField = useField({ initialValue: "", validate: (v) => (v.trim().length < 1 ? "Enter a code" : null) });

    return (
        <Stack>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    verifyField
                        .validate()
                        .then(() => verifyEudAsync({ userId, eudId, code: verifyField.getValue() }))
                        .then((data) => {
                            if (!data?.verifyEUD.isVerified) {
                                notify.show.error({ message: "Incorrect verification code" });
                                return;
                            }
                            notify.show.success("Device verified");
                            modals.closeAll();
                        })
                        .catch(logger.error);
                }}
            >
                <Stack>
                    <Text>
                        We sent a code to <em>{eudValue.replace(";", " ext. ").replaceAll("W", "")}</em>. This code is only active for the
                        next 15 minutes.
                    </Text>
                    <TextInput label="Enter Code" required placeholder="Enter code..." {...verifyField.getInputProps()} />
                    <Button
                        variant="default"
                        w="100%"
                        onClick={(e) => {
                            e.preventDefault();
                            resendVerifyEud({ userId, eudID: eudId });
                        }}
                    >
                        Resend Code
                    </Button>
                    <Button w="100%" type="submit">
                        Confirm
                    </Button>
                </Stack>
            </form>
        </Stack>
    );
};

function cmToLabel(type: Cm) {
    return match(type)
        .with(Cm.Email, () => "Email")
        .with(Cm.Sms, () => "SMS Phone Number")
        .with(Cm.Phone, () => "Voice Phone Number")
        .with(Cm.Android, () => "Android")
        .with(Cm.Ios, () => "iOS")
        .with(Cm.Webhook, () => "Webhook")
        .exhaustive();
}

function compareEuds(a: EudLikeFragment, b: EudLikeFragment) {
    return a.value.localeCompare(b.value);
}

const EudDetails = ({ eud, notVerifiedOnClick }: { eud: EudLikeFragment; notVerifiedOnClick: (type: Cm) => void }) => {
    const { user } = useUser({ assert: true });
    const { removeEudAsync } = useRemoveEud();

    const setEudInformational = useGraphqlMutation(
        graphql(`
            mutation SettingsMarkEudAsInformational($userId: UUID!, $eudId: UUID!, $isInformational: Boolean!) {
                setDefaultEud(userId: $userId, eudId: $eudId, isDefault: $isInformational) {
                    id
                }
            }
        `),
    );
    const hasAtLeastTwoSendableEmails = (user?.euds.filter((it) => it.type == Cm.Email && it.isVerified && it.isDefault) ?? []).length > 1;

    function disableRemoveEudInformational() {
        if (!features.enabled("HAS_CM_DELIVERY_OVERRIDES")) return false;
        return eud.isVerified && eud.isDefault && eud.type == Cm.Email && !hasAtLeastTwoSendableEmails;
    }

    const eudTypeLabelId = `eud-type-label-${eud.id}`;

    return (
        <Stack>
            <Group wrap="nowrap" style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }} align="flex-end">
                <Stack gap="xs" miw={10} style={{ flexShrink: 1 }}>
                    <Group align="center">
                        <Text fz="sm" id={eudTypeLabelId}>
                            {cmToLabel(eud.type)}:
                        </Text>

                        {!eud.isVerified ? (
                            <Badge
                                color="red"
                                disabled={eud.optOut.system}
                                onClick={() => notVerifiedOnClick(eud.type)}
                                component="button"
                                style={{ cursor: eud.optOut.system ? "not-allowed" : "pointer" }}
                            >
                                Not Verified
                            </Badge>
                        ) : (
                            <Badge color="green" style={{ flexShrink: 0 }}>
                                Verified
                            </Badge>
                        )}
                        {eud.optOut.sms.length > 0 && (
                            <Badge
                                variant="light"
                                color="orange"
                                component="button"
                                rightSection={<IconInfoCircle size={16} />}
                                style={{ cursor: "pointer" }}
                                onClick={() => openSmsOptOutModal({ value: eud.value, optOuts: eud.optOut.sms })}
                            >
                                Opted Out
                            </Badge>
                        )}
                    </Group>
                    <TextInput
                        readOnly
                        variant="filled"
                        value={(eud.type == Cm.Ios || eud.type == Cm.Android ? eud.name : eud.value) ?? ""}
                        aria-labelledby={eudTypeLabelId}
                    />
                </Stack>
                <ActionIcon
                    size="input-sm"
                    variant="transparent"
                    disabled={disableRemoveEudInformational()}
                    onClick={() => {
                        void notify
                            .confirm("Are you sure you want to remove this device?", { title: "Confirm Delete", color: "red" })
                            .then(async ({ close }) => {
                                await removeEudAsync({ userId: user.id, eudId: eud.id });
                                close();
                            });
                    }}
                    aria-label="Remove E.U.D."
                >
                    <IconTrash size={16} />
                </ActionIcon>
            </Group>
            {eud.isVerified ? (
                <Stack>
                    <Group>
                        <Checkbox
                            label="All Messages"
                            defaultChecked={eud.isDefault}
                            disabled={disableRemoveEudInformational()}
                            onClick={(e) =>
                                void setEudInformational
                                    .mutateAsync({ userId: user.id, eudId: eud.id, isInformational: e.currentTarget.checked })
                                    .then(() => qk.invalidate("user", "id", user.id))
                                    .then(() => notify.show.success("Updated preferences"))
                            }
                        />
                        <Tooltip label="All registered devices will receive urgent notifications">
                            <Checkbox label="Urgent Messages" checked disabled />
                        </Tooltip>
                    </Group>
                    <Text fz="sm" fs="italic">
                        {match(eud.type)
                            .with(
                                Cm.Android,
                                () =>
                                    "Urgent messages will be sent to all devices with the app installed.  Non-urgent messages will be sent to this device if selected.",
                            )
                            .with(
                                Cm.Ios,
                                () =>
                                    "Urgent messages will be sent to all devices with the app installed.  Non-urgent messages will be sent to this device if selected.",
                            )
                            .with(
                                Cm.Email,
                                () =>
                                    "Urgent messages will be sent to all emails provided.  Non-urgent messages will be sent to this email address if selected.",
                            )
                            .with(
                                Cm.Sms,
                                () =>
                                    "Urgent messages will be sent to all text numbers provided.  Non-urgent messages will be sent to this number if selected.",
                            )
                            .with(
                                Cm.Phone,
                                () =>
                                    "Urgent messages will be sent to all numbers provided.  Non-urgent messages will be sent to this number if selected.",
                            )
                            .with(
                                Cm.Webhook,
                                () =>
                                    "Urgent messages will be sent to all webhooks provided.  Non-urgent messages will be sent to this webhook if selected.",
                            )
                            .exhaustive()}
                    </Text>
                </Stack>
            ) : (
                // Assumes App euds are always verified
                <Button disabled={eud.optOut.system} onClick={() => notVerifiedOnClick(eud.type)}>
                    Send Verification Code
                </Button>
            )}
        </Stack>
    );
};

export const EmailEuds = () => {
    const { userId } = useUserId();
    const { euds } = useUserEuds();
    const { resendVerifyEud } = useResendVerifyEud();

    const emailEUDs = euds.filter((eud) => eud.type == Cm.Email);
    const [showAddForm, setShowAddForm] = useState(emailEUDs.length == 0);

    return (
        <Stack>
            {emailEUDs.sort(compareEuds).map((eud) => (
                <>
                    <EudDetails
                        key={`${eud.type}${eud.name}${eud.value}`}
                        eud={eud}
                        notVerifiedOnClick={() => {
                            resendVerifyEud({ userId, eudID: eud.id });
                            openVerifyEudModal({ type: Cm.Email, value: eud.value, id: eud.id });
                        }}
                    />
                    <Divider />
                </>
            ))}
            {showAddForm && <AddEudForm deviceType={Cm.Email} onSubmit={() => setShowAddForm(false)} />}
            {emailEUDs.length < 3 && (
                <Button leftSection={<IconPlus size={16} />} variant="default" w="min-content" onClick={() => setShowAddForm(true)}>
                    Add Email
                </Button>
            )}
        </Stack>
    );
};

export const TextEuds = () => {
    const { userId } = useUserId();
    const { euds } = useUserEuds();
    const { resendVerifyEud } = useResendVerifyEud();

    const smsEUDs = euds.filter((eud) => eud.type == Cm.Sms);
    const [showAddForm, setShowAddForm] = useState(smsEUDs.length == 0);

    return (
        <Stack>
            {smsEUDs.sort(compareEuds).map((eud) => (
                <>
                    <EudDetails
                        eud={eud}
                        key={`${eud.type}${eud.name}${eud.value}`}
                        notVerifiedOnClick={(type) => {
                            resendVerifyEud({ userId, eudID: eud.id });
                            openVerifyEudModal({ type, value: eud.value, id: eud.id });
                        }}
                    />
                    <Divider />
                </>
            ))}
            {showAddForm && <AddEudForm deviceType={Cm.Sms} onSubmit={() => setShowAddForm(false)} />}
            {smsEUDs.length < 3 && (
                <Button leftSection={<IconPlus size={16} />} variant="default" w="min-content" onClick={() => setShowAddForm(true)}>
                    Add Text/SMS Phone Number
                </Button>
            )}
        </Stack>
    );
};

export const VoiceEuds = () => {
    const { userId } = useUserId();
    const { euds } = useUserEuds();
    const { resendVerifyEud } = useResendVerifyEud();

    const voiceEUDs = euds.filter((eud) => eud.type == Cm.Phone);
    const [showAddForm, setShowAddForm] = useState(voiceEUDs.length == 0);

    return (
        <Stack>
            {voiceEUDs.sort(compareEuds).map((eud) => (
                <>
                    <EudDetails
                        eud={eud}
                        key={`${eud.type}${eud.name}${eud.value}`}
                        notVerifiedOnClick={(type) => {
                            resendVerifyEud({ userId, eudID: eud.id });
                            openVerifyEudModal({ type, value: eud.value, id: eud.id });
                        }}
                    />
                    <Divider />
                </>
            ))}
            {showAddForm && <AddEudForm deviceType={Cm.Phone} onSubmit={() => setShowAddForm(false)} />}
            {voiceEUDs.length < 3 && (
                <Button leftSection={<IconPlus size={16} />} variant="default" w="min-content" onClick={() => setShowAddForm(true)}>
                    Add Voice Phone Number
                </Button>
            )}
        </Stack>
    );
};

export const AppEuds = () => {
    const { euds } = useUserEuds();

    const appEUDs = euds.filter((eud) => eud.type == Cm.Android || eud.type == Cm.Ios);

    return (
        <Stack>
            {appEUDs.sort(compareEuds).map((eud) => (
                <>
                    <EudDetails eud={eud} key={`${eud.type}${eud.name}${eud.value}`} notVerifiedOnClick={() => {}} />
                    <Divider />
                </>
            ))}
            <Group>
                <Anchor target="_blank" href="https://apps.apple.com/us/app/bryx-alerts/id6742207308">
                    <Image src="/apple_store_badge.svg" h={40} alt="Download on the App Store" />
                </Anchor>
                <Anchor target="_blank" href="https://play.google.com/store/apps/details?id=com.bryx.emns">
                    <Image src="/google_store_badge.png" h={40} alt="Get it on Google Play" />
                </Anchor>
            </Group>
        </Stack>
    );
};

function openSmsOptOutModal(opts: { value: string; optOuts: string[] }) {
    modals.open({
        title: opts.value + " - Opted Out",
        children: (
            <Stack>
                <Text size="sm">
                    You have opted out of receiving text messages from the following number{opts.optOuts.length > 1 ? "s" : ""}:
                </Text>

                <div>
                    {opts.optOuts.map((number) => (
                        <Text fw="bold" key={number}>
                            {number}
                        </Text>
                    ))}
                </div>

                <Text size="sm">To opt back in, send "START" to the number above you would like to begin receiving messages from.</Text>

                <Text size="xs" fs="italic">
                    You may still receive text messages from other numbers within the system. To no longer receive any text messages use the
                    '{<IconTrash style={{ marginBottom: "-4px" }} size={16} />}' to remove this text number.
                </Text>
            </Stack>
        ),
    });
}

z.number().min(Date.now());
